import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { TextFieldWithLabel } from '../FormFieldControls/TextFieldWithLabel';
import { httpGet, httpPost, httpDelete } from '../../HttpRequestHandling/httpRequestHandler';
import { toast } from 'react-toastify';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

export const CompanyIdentifier = ({ company, ...props }) => {
    const containerStyle = {
        marginTop: '20px',
        marginLeft: 'auto',
        marginRight: 'auto',
        maxWidth: '48%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    };

    const defaultItemStyle = {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        flexWrap: 'wrap',
        width: '100%',
        justifyContent: 'space-between'
    };

    const editingItemStyle = {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        flexWrap: 'wrap',
        width: '208%',
        justifyContent: 'space-between'
    };

    const identifierStyle = {
        flex: 1,
        fontSize: '18px',
        fontWeight: 'bold',
        minWidth: '150px'
    };

    const buttonContainerStyle = {
        display: 'flex',
        width: '51%'
    };

    const [isAdding, setIsAdding] = useState(false);
    const [identifier, setIdentifier] = useState('');
    const [identifiers, setIdentifiers] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(null);

    useEffect(() => {
        fetchIdentifiers();
    }, []);

    useEffect(() => {
        if (isAdding || editingIndex !== null) {
            setTimeout(() => {
                const inputElement = document.querySelector(`#${identifierFieldInfo.textboxId}`);
                if (inputElement) {
                    inputElement.focus();
                }
            }, 0);
        }
    }, [isAdding, editingIndex]);

    const fetchIdentifiers = () => {
        props.setInProgress(true);
        httpGet(`/api/company/getidentifier/${company.companyCode}`)
            .then((response) => {
                setIdentifiers(response.data);
                props.setInProgress(false);
            })
            .catch((error) => {
                toast.error('Failed to fetch identifiers');
                props.setInProgress(false);
            });
    };

    const handleAddClick = () => {
        setIsAdding(true);
        setEditingIndex(null);
        setIdentifier('');
    };

    const handleCheckClick = () => {
        if (!identifier.trim()) {
            toast.error('Identifier cannot be empty');
            return;
        }
        const originalIdentifier = editingIndex !== null ? identifiers[editingIndex].companyIdentifier : null;
        const companyIdentifierData = {
            companyCode: company.companyCode,
            companyIdentifier: originalIdentifier,
            newCompanyIdentifier: identifier
        };

        props.setInProgress(true);
        httpPost('/api/company/CompanyIdentifier', companyIdentifierData)
            .then(() => {
                toast.success('Company identifier updated successfully');
                fetchIdentifiers();
                setIsAdding(false);
                setIdentifier('');
                setEditingIndex(null);
            })
            .catch((error) => {
                const errorMessage = error.response?.data || '';
                if (errorMessage.includes('Duplicate entry')) {
                    toast.error('Update failed: Duplicate entry');
                } else {
                    toast.error(<div>Update Failed<br />{errorMessage}</div>);
                }
            })
            .finally(() => {
                props.setInProgress(false);
            });
    };

    const handleEditClick = (index) => {
        setIsAdding(true);
        setEditingIndex(index);
        setIdentifier(identifiers[index].companyIdentifier);
    };

    const handleDeleteClick = (index) => {
        setDeleteIndex(index);
        setOpenDeleteConfirmation(true);
    };

    const deleteRecord = () => {
        const identifierToDelete = identifiers[deleteIndex];
        const url = `/api/company/deletecompanyidentifier/${identifierToDelete.companyCode}?companyIdentifier=${encodeURIComponent(identifierToDelete.companyIdentifier)}`;

        props.setInProgress(true);
        httpDelete(url)
            .then(() => {
                toast.success('Company identifier deleted successfully');
                fetchIdentifiers();
                setOpenDeleteConfirmation(false);
            })
            .catch((error) => {
                toast.error(`Delete failed: ${error.response.data}`);
            })
            .finally(() => {
                props.setInProgress(false);
            });
    };

    const closeDeleteDialog = () => {
        setOpenDeleteConfirmation(false);
    };

    const handleCloseClick = () => {
        setIsAdding(false);
        setIdentifier('');
        setEditingIndex(null);
    };

    const identifierFieldInfo = {
        labelId: 'lblNewIdentifier',
        labelText: isAdding && editingIndex === null ? 'New Identifier' : 'Edit Identifier',
        textboxPlaceholder: isAdding && editingIndex === null ? 'New Identifier' : 'Edit Identifier',
        textboxId: 'txtNewIdentifier',
        readOnly: false,
        textboxValue: identifier,
        fieldToFocus: 'txtNewIdentifier'
    };

    return (
        <div style={{ marginRight: '20px' }}>
            {!isAdding && editingIndex === null && (
                <Button
                    variant="outlined"
                    sx={{
                        color: 'white',
                        backgroundColor: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.dark' },
                        marginLeft: '14px',
                        textTransform: 'none'
                    }}
                    onClick={handleAddClick}
                >
                    Add New Company Identifier
                </Button>
            )}
            <div style={containerStyle}>
                {identifiers.map((item, index) => (
                    <div key={index} style={editingIndex === index ? editingItemStyle : defaultItemStyle}>
                        {editingIndex === index ? (
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: '10px' }}>
                                <TextFieldWithLabel
                                    info={identifierFieldInfo}
                                    changeHandler={(e) => setIdentifier(e.target.value.slice(0, 1024))}
                                    inputProps={{ maxLength: 1024 }}
                                    style={{ flex: 1, marginRight: '10px', width: '100%' }}
                                />
                                <IconButton color="primary" onClick={handleCheckClick}>
                                    <CheckIcon />
                                </IconButton>
                                <IconButton color="secondary" style={{ color: 'red' }} onClick={handleCloseClick}>
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        ) : (
                                <>
                                    <span style={identifierStyle} title={item.companyIdentifier}>
                                        {item.companyIdentifier.length > 35 ? item.companyIdentifier.slice(0, 35) + '...' : item.companyIdentifier}
                                    </span>
                                <div style={buttonContainerStyle}>
                                    <Button onClick={() => handleEditClick(index)} style={{ marginRight: '10px', textTransform: 'none' }}>
                                        Edit
                                    </Button>
                                    <Button onClick={() => handleDeleteClick(index)} color="secondary" style={{ textTransform: 'none', color: 'red' }}>
                                        Delete
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
            
            {isAdding && editingIndex === null && (
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: '10px' }}>
                    <TextFieldWithLabel
                        info={identifierFieldInfo}
                        changeHandler={(e) => setIdentifier(e.target.value.slice(0, 1024))}
                        inputProps={{ maxLength: 1024 }}
                        style={{ flex: 1, marginRight: '10px', width: '100%' }}
                    />
                    <IconButton color="primary" onClick={handleCheckClick}>
                        <CheckIcon />
                    </IconButton>
                    <IconButton color="secondary" style={{ color: 'red' }} onClick={handleCloseClick}>
                        <CloseIcon />
                    </IconButton>
                </div>
            )}

            <Dialog
                open={openDeleteConfirmation}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    Delete Records
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={deleteRecord} color="primary">
                        Yes
                    </Button>
                    <Button autoFocus onClick={closeDeleteDialog} color="primary">
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};